import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { mediaUrl } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CalendarDays, MapPin, ArrowRight } from "lucide-react";

export const Hero = ({ settings, onBegin }) => {
  const { L, t, lang } = useLang();

  return (
    <section
      className="relative flex min-h-[100svh] w-full flex-col overflow-hidden bg-[#F7F4EE]"
      data-testid="hero-section"
    >
      {/* Cinematic image — the emotional centerpiece */}
      <div className="absolute inset-0">
        <img
          src={mediaUrl(settings.heroImage)}
          alt="The Ka'bah and the Makkah Clock Tower during Tawaf"
          className="h-full w-full object-cover hero-zoom"
          style={{ objectPosition: "center 82%" }}
          loading="eager"
        />
        {/* subtle top wash so the navy logo stays legible */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/25 to-transparent" />
        {/* single seamless gradient: dark scrim for text that dissolves into the page color at the very bottom */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, #F7F4EE 0%, rgba(247,244,238,0) 11%, rgba(6,12,24,0.72) 28%, rgba(6,12,24,0.45) 48%, rgba(6,12,24,0) 74%)",
          }}
        />
      </div>

      {/* Top bar — original logo, transparent, generous padding */}
      <div className="relative z-20 flex items-start justify-between px-6 pt-7 sm:px-8 sm:pt-8">
        <img
          src="/maqam-logo.png"
          alt="Maqam"
          className="h-11 sm:h-12 w-auto object-contain animate-fade-in"
          draggable={false}
        />
        <LanguageSwitcher variant="dark" />
      </div>

      {/* Overlaid content — lifted upward so the image breathes */}
      <div className="relative z-10 mt-auto px-6 pb-[20vh] sm:px-8 sm:pb-[17vh]">
        <div className="mx-auto w-full max-w-xl">
          <h1
            className="font-display text-[2.6rem] leading-[1.04] sm:text-[3.2rem] font-extrabold tracking-tight text-white animate-fade-up"
            style={{ animationDelay: "0.12s", textShadow: "0 3px 26px rgba(0,0,0,0.7)" }}
          >
            {t("hero.welcome")}
            <br />
            <span className="text-[#E9CE7D]">{t("hero.brand")}</span>
          </h1>

          <button
            onClick={onBegin}
            data-testid="begin-journey-btn"
            className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-[1.25rem] bg-[#0A192F] px-8 py-[1.25rem] text-base font-bold text-white shadow-[0_18px_44px_-12px_rgba(0,0,0,0.75)] ring-1 ring-white/10 transition-all duration-300 hover:bg-[#112240] active:scale-[0.98] animate-fade-up sm:w-auto sm:px-14"
            style={{ animationDelay: "0.24s" }}
          >
            {(settings.ctaLabel && settings.ctaLabel[lang]) || t("beginJourney")}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>

          {/* Trip meta — higher contrast pills */}
          <div
            className="mt-6 flex flex-wrap items-center gap-2.5 animate-fade-up"
            style={{ animationDelay: "0.34s" }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-black/35 px-3.5 py-1.5 backdrop-blur-md ring-1 ring-white/15">
              <CalendarDays size={16} className="text-[#E9CE7D]" strokeWidth={2.5} />
              <span className="text-sm font-semibold text-white">{L(settings.tripDates)}</span>
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-black/35 px-3.5 py-1.5 backdrop-blur-md ring-1 ring-white/15">
              <MapPin size={16} className="text-[#E9CE7D]" strokeWidth={2.5} />
              <span className="text-sm font-semibold text-white">{L(settings.destination)}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
